import React, { useState } from 'react';
import './Login.css';
import { Link, useNavigate } from "react-router-dom";

import hayatlogo from '../assets/images/hayatlogo.jpg';
import hayatin from '../assets/images/hayatin.jpg'
import emailicon from '../assets/images/emailicon.jpg'
import passwordicon from '../assets/images/passwordicon.jpg'
import passwordshowicon from '../assets/images/passwordshowicon.jpg'
import google from '../assets/images/google.jpg'
import facebook from '../assets/images/facebook.jpg'
import ios from '../assets/images/ios.jpg'
import axios from 'axios';
import { BACKEND_URL } from './common/Common';

const Login = () => {
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword(!showPassword);
    const [sociallogin, setSocialLogin] = useState([]);
    const [isSocialLoading, setIsSocialLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        rememberMe: false
    });

    // Validation errors
    const [errors, setErrors] = useState({
        email: "",
        password: ""
    });

    const [isLoading, setIsLoading] = useState(false);

    // Detect platform/device type
    const detectPlatform = () => {
        const userAgent = navigator.userAgent || navigator.platform || '';
        const platform = navigator.platform || '';

        // Check for iOS (iPhone, iPad, iPod)
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

        // Check for Mac
        const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(platform);

        // Check for Android
        const isAndroid = /Android/.test(userAgent);

        if (isIOS || isMac) {
            return 'ios';
        } else if (isAndroid) {
            return 'android';
        } else {
            return 'web'; // Web/Desktop (Windows, Linux, etc.)
        }
    };

    const [platform, setPlatform] = useState(detectPlatform());

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    // Validation functions
    const validateEmail = (email) => {
        if (!email.trim()) {
            return "Email is required";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return "Please enter a valid email address";
        }
        return "";
    };

    const validatePassword = (password) => {
        if (!password) {
            return "Password is required";
        }
        return "";
    };

    // Validate all fields
    const validateForm = () => {
        const newErrors = {
            email: validateEmail(formData.email),
            password: validatePassword(formData.password)
        };

        setErrors(newErrors);

        // Check if there are any errors
        return !Object.values(newErrors).some(error => error !== "");
    };

    const handleLogin = async () => {
        const payload = {
            email: formData.email.trim(),
            password: formData.password,
            // fcm_token: "" // Optional
        };

        setIsLoading(true);
        try {
            const response = await axios.post(`${BACKEND_URL}website/login`, payload);
            // const response = await axios.post(`http://localhost:8000/api/website/login`, payload);
            console.log("Login Response:", response.data);

            // Store authentication data
            if (response.data) {
                // Store user_id from user.id (API response structure: user.id)
                if (response.data.user && response.data.user.id) {
                    localStorage.setItem('user_id', response.data.user.id);
                }
                
                // Store auth_token (API response has auth_token, not token)
                if (response.data.auth_token) {
                    localStorage.setItem('token', response.data.auth_token);
                }
                
                // Store user object
                if (response.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                // If remember me is checked, also store email (optional)
                if (formData.rememberMe) {
                    localStorage.setItem('rememberedEmail', formData.email);
                } else {
                    localStorage.removeItem('rememberedEmail');
                }

                // Dispatch event to notify other components (Navbar, Home) about login state change
                // This is the same pattern used in OtpPage.js after successful registration
                if (response.data.user && response.data.user.id) {
                    window.dispatchEvent(new Event('userLoginStateChanged'));
                }
            }

            // Small delay to ensure localStorage is updated before navigation (same as registration)
            setTimeout(() => {
                // Navigate to home on success
                navigate("/");
            }, 100);
        } catch (error) {
            console.log("Error Fetching Login Data", error);

            // Handle error messages
            let errorMessage = "Login failed. Please check your credentials and try again.";

            if (error.response && error.response.data) {
                const errorData = error.response.data;
                errorMessage = errorData.message || errorData.error || errorMessage;

                // Check for specific field errors
                if (errorData.email) {
                    setErrors(prev => ({ ...prev, email: errorData.email }));
                }
                if (errorData.password) {
                    setErrors(prev => ({ ...prev, password: errorData.password }));
                }
            }

            // Show general error if no specific field errors
            if (!error.response?.data?.email && !error.response?.data?.password) {
                alert(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        if (!validateForm()) {
            return;
        }

        // Call login API
        await handleLogin();
    };

    // Handle Google Sign-In
    const handleGoogleSignIn = async () => {
        setIsSocialLoading(true);
        try {
            // Wait for Google script to load if not already loaded
            if (!window.google || !window.google.accounts) {
                // Wait up to 5 seconds for script to load
                let attempts = 0;
                while (!window.google && attempts < 50) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    attempts++;
                }

                if (!window.google || !window.google.accounts) {
                    alert('Google Sign-In is loading. Please wait a moment and try again.');
                    setIsSocialLoading(false);
                    return;
                }
            }

            // Get Google Client ID from environment
            const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

            if (!googleClientId) {
                alert('Google Sign-In is not configured. Please add REACT_APP_GOOGLE_CLIENT_ID to your .env file or use email/password login.');
                setIsSocialLoading(false);
                return;
            }

            // Use OAuth2 popup flow for custom button
            const tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: googleClientId,
                scope: 'email profile',
                callback: async (tokenResponse) => {
                    try {
                        if (tokenResponse.access_token) {
                            // Fetch user info from Google API
                            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
                                headers: {
                                    'Authorization': `Bearer ${tokenResponse.access_token}`
                                }
                            });

                            if (userInfoResponse.ok) {
                                const userInfo = await userInfoResponse.json();
                                await handleSocialLogin('google', {
                                    email: userInfo.email || '',
                                    first_name: userInfo.given_name || userInfo.name || '',
                                });
                            } else {
                                throw new Error('Failed to fetch user info from Google');
                            }
                        } else {
                            setIsSocialLoading(false);
                        }
                    } catch (error) {
                        console.error('Google Sign-In error:', error);
                        alert('Failed to sign in with Google. Please try again.');
                        setIsSocialLoading(false);
                    }
                },
            });

            // Request access token (this opens the popup)
            tokenClient.requestAccessToken({ prompt: 'consent' });

        } catch (error) {
            console.error('Error initializing Google Sign-In:', error);
            alert('Failed to initialize Google Sign-In. Please try again.');
            setIsSocialLoading(false);
        }
    };

    // Handle Apple Sign-In
    const handleAppleSignIn = async () => {
        setIsSocialLoading(true);
        try {
            // Initialize Apple Sign-In
            // Note: You'll need to configure Apple Sign-In in your project
            if (window.AppleID && window.AppleID.auth) {
                window.AppleID.auth.init({
                    clientId: process.env.REACT_APP_APPLE_CLIENT_ID || '', // Add your Apple Client ID
                    scope: 'name email',
                    redirectURI: window.location.origin,
                    usePopup: true
                });

                const response = await window.AppleID.auth.signIn();

                if (response.authorization) {
                    await handleSocialLogin('apple', {
                        email: response.user?.email || '',
                        first_name: response.user?.name?.firstName || '',
                    });
                }
            } else {
                // Fallback: Manual Apple Sign-In flow
                alert('Apple Sign-In is being configured. Please use email/password for now.');
            }
        } catch (error) {
            console.error('Error with Apple Sign-In:', error);
            alert('Failed to sign in with Apple. Please try again.');
        } finally {
            setIsSocialLoading(false);
        }
    };

    // Handle Facebook Sign-In
    const handleFacebookSignIn = async () => {
        setIsSocialLoading(true);
        try {
            // Initialize Facebook Sign-In
            // Note: You'll need to configure Facebook SDK in your project
            if (window.FB) {
                window.FB.login(async (response) => {
                    if (response.authResponse) {
                        // Get user info from Facebook
                        window.FB.api('/me', { fields: 'name,email,first_name' }, async (userInfo) => {
                            await handleSocialLogin('facebook', {
                                email: userInfo.email || '',
                                first_name: userInfo.first_name || userInfo.name || '',
                            });
                        });
                    } else {
                        alert('Facebook login was cancelled or failed.');
                    }
                }, { scope: 'email,public_profile' });
            } else {
                // Fallback: Manual Facebook Sign-In flow
                alert('Facebook Sign-In is being configured. Please use email/password for now.');
            }
        } catch (error) {
            console.error('Error with Facebook Sign-In:', error);
            alert('Failed to sign in with Facebook. Please try again.');
        } finally {
            setIsSocialLoading(false);
        }
    };

    // Common social login handler
    const handleSocialLogin = async (provider, userData) => {
        try {
            const payload = {
                email: userData.email,
                first_name: userData.first_name || '',
                device_type: platform, // 'ios', 'android', or 'web'
                // fcm_token: "", // Optional - get from device if available
                // device_cart_id: "", // Optional
            };

            const response = await axios.post(`${BACKEND_URL}website/social-login`, payload);
            console.log("Social Login Response:", response.data);
            setSocialLogin(response.data);

            // Store authentication data (similar to regular login)
            if (response.data) {
                // Store user_id from user.id (API response structure: user.id)
                if (response.data.user && response.data.user.id) {
                    localStorage.setItem('user_id', response.data.user.id);
                }
                
                // Store auth_token (API response has auth_token, not token)
                if (response.data.auth_token) {
                    localStorage.setItem('token', response.data.auth_token);
                }
                
                // Store user object
                if (response.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                // Dispatch event to notify other components (Navbar, Home) about login state change
                // This is the same pattern used in OtpPage.js after successful registration
                if (response.data.user && response.data.user.id) {
                    window.dispatchEvent(new Event('userLoginStateChanged'));
                }
            }

            // Small delay to ensure localStorage is updated before navigation (same as registration)
            setTimeout(() => {
                // Navigate to home on success
                navigate("/");
            }, 100);
        } catch (error) {
            console.log("Error Fetching the Social Login Data:", error);

            let errorMessage = "Social login failed. Please try again.";
            if (error.response && error.response.data) {
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            }
            alert(errorMessage);
        } finally {
            setIsSocialLoading(false);
        }
    };

    // Handle social icon click based on platform
    const handleSocialIconClick = (iconType) => {
        if (isSocialLoading) return; // Prevent multiple clicks

        if (iconType === 'google') {
            handleGoogleSignIn();
        } else if (iconType === 'apple' || iconType === 'ios') {
            handleAppleSignIn();
        } else if (iconType === 'facebook') {
            handleFacebookSignIn();
        }
    };

    // Load Google Identity Services script
    React.useEffect(() => {
        // Load Google Identity Services script if not already loaded
        if (!window.google) {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log('Google Identity Services loaded');
            };
            script.onerror = () => {
                console.error('Failed to load Google Identity Services');
            };
            document.body.appendChild(script);
        }
    }, []);

    // Decode JWT token to get user info
    const decodeJWT = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error decoding JWT:', error);
            return null;
        }
    };

    // Load remembered email on mount
    React.useEffect(() => {
        const rememberedEmail = localStorage.getItem('rememberedEmail');
        if (rememberedEmail) {
            setFormData(prev => ({
                ...prev,
                email: rememberedEmail,
                rememberMe: true
            }));
        }
    }, []);
    return (
        <div className="login-container">
            <div className="register-left">
                <img src={hayatlogo} alt="Register" />
            </div>

            <div className='register-right'>
                <div className='rightside-header'>
                    <img src={hayatin} alt='hayatin' />
                    <div className='signin-account'>
                        <p>New User?</p>
                        <Link to="/register" className="signin-link">
                            Create Account
                        </Link>
                    </div>
                </div>

                <div className='center-banner'>
                    <div className='register-heading'>
                        <h4>Good to See You Again!</h4>
                    </div>
                    <div className='form-container'>
                        <form onSubmit={handleSubmit}>
                            <div className="input-group">
                                <h6>Email <span>*</span></h6>
                                <div className='input-field'>
                                    <img src={emailicon} alt='emailicon' className="input-icon" />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter Your Email"
                                    />
                                </div>
                                {errors.email && <span style={{ color: 'red', fontSize: '12px', marginTop: '5px', display: 'block' }}>{errors.email}</span>}
                            </div>

                            <div className="input-group">
                                <h6>Password <span>*</span></h6>
                                <div className='input-field'>
                                    <img src={passwordicon} alt='passwordicon' className="input-icon" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="Enter Your Password"
                                    />
                                    <img
                                        src={passwordshowicon}
                                        alt='passwordshowicon'
                                        className="passwordshow-icon"
                                        onClick={togglePassword}
                                        style={{ cursor: "pointer" }}
                                    />
                                </div>
                                {errors.password && <span style={{ color: 'red', fontSize: '12px', marginTop: '5px', display: 'block' }}>{errors.password}</span>}
                            </div>

                            <div className='remember-checkbox'>
                                <div className='checkbox-content'>
                                    <input
                                        type="checkbox"
                                        name="rememberMe"
                                        checked={formData.rememberMe}
                                        onChange={handleInputChange}
                                    />
                                    <h6>Remember Me </h6>
                                </div>
                                <Link to="/forgot-password" id="forgot-password">
                                    Forgot Password
                                </Link>
                            </div>

                            <div className='createaccount-button'>
                                <button
                                    type="submit"
                                    className="create-account-btn"
                                    disabled={isLoading}
                                >
                                    Sign In
                                </button>
                            </div>
                        </form>
                        <div className="divider">
                            <hr /><p>Continue With</p><hr />
                        </div>
                        <div className='socialicon'>
                            {/* Google icon - works on Android and Web */}
                            <img
                                src={google}
                                alt='google'
                                onClick={() => {
                                    if (platform === 'android' || platform === 'web') {
                                        handleSocialIconClick('google');
                                    } else {
                                        alert('Google Sign-In is available on Android and Web. Please use Apple Sign-In or email/password.');
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                                title="Sign in with Google (Android/Web)"
                            />
                            {/* Facebook icon - works on all platforms */}
                            <img
                                src={facebook}
                                alt='facebook'
                                onClick={() => handleSocialIconClick('facebook')}
                                style={{ cursor: 'pointer' }}
                                title="Sign in with Facebook"
                            />
                            {/* iOS/Apple icon - works on iOS and Mac */}
                            <img
                                src={ios}
                                alt='apple'
                                onClick={() => {
                                    if (platform === 'ios') {
                                        handleSocialIconClick('apple');
                                    } else {
                                        alert('Apple Sign-In is available on iOS and Mac. Please use Google Sign-In or email/password.');
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                                title="Sign in with Apple (iOS/Mac)"
                            />
                        </div>
                        {isSocialLoading && (
                            <div style={{ textAlign: 'center', marginTop: '10px', color: '#666' }}>
                                Signing in...
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
